import FormUtils from 'chairisher/util/form';
import KeyUtils from 'chairisher/util/key';
import ProductModel from 'chairisher/model/product';

import { getAmplitudeEventPropertiesForProduct } from 'chairisher/analytics/product';
import { getProductObject } from 'chairisher/context/product';
import { getValidationEndpointUrl } from 'chairisher/context/purchase';
import { AmplitudeEventProperty, CheckoutPosition, logAmplitudeButtonClickEvent } from 'chairisher/util/analytics';
import { postJSON } from 'chairisher/util/fetch';
import { handleAsyncError } from 'chairisher/util/logging';
import { getDiscountedPrice, getPercentOff } from 'chairisher/util/math';

/**
 * Only returns a percentage value if the inputted price is within the valid range.
 *
 * @param {number} inputPrice The inputted offer price
 * @param {number} listPrice The list price for the product
 * @returns {number|string} The calculated percent off; empty string, if invalid
 */
function getValidPercentValue(inputPrice, listPrice) {
    return inputPrice >= 0 && inputPrice <= listPrice ? getPercentOff(listPrice, inputPrice) : '';
}

/**
 * Updates the price and percent input values
 *
 * @param {jQuery} $priceInput The price input field
 * @param {number} inputPrice The inputted offer price
 * @param {number} listPrice The list price for the product
 */
export function updateInputValues($priceInput, inputPrice, listPrice) {
    $priceInput.val(inputPrice);
    const $percentInput = $priceInput.siblings('input.js-percent-input-group');
    $percentInput.val(getValidPercentValue(inputPrice, listPrice));
}

/**
 * Submit an ajax request to the offer validation endpoint to check if the price is valid.
 *
 * @param {object} settings
 * @param {number} settings.price
 * @param {number} settings.productId
 * @param {number} settings.quantity
 * @returns {Promise}
 */
function validateOfferPrice({ price, productId, quantity }) {
    return postJSON(getValidationEndpointUrl(), {
        products: [
            {
                id: productId,
                price,
                quantity,
            },
        ],
    });
}

/**
 * View with a form for creating custom offer prices.
 *
 * @param {Object} settings
 * @param {JQuery} settings.$el
 * @param {String} settings.formSelector
 * @param {Function=} settings.onPriceChange
 * @param {Function} settings.onSubmit
 * @param {Number} settings.productId
 * @param {number|null} settings.privateDealId
 * @param {Boolean=} settings.shouldValidateOnBlur
 */
export default class PriceInputView {
    constructor({
        $el,
        formSelector,
        onPriceChange = () => {},
        onSubmit,
        productId,
        privateDealId = null,
        shouldValidateOnBlur = true,
    }) {
        this.$el = $el;
        this.formSelector = formSelector;
        this.onPriceChange = onPriceChange;
        this.onSubmit = onSubmit;
        this.productId = productId;
        this.privateDealId = privateDealId;
        this.shouldValidateOnBlur = shouldValidateOnBlur;
    }

    bind() {
        this.$percentInput = this.$el.find('input.js-percent-input-group');
        this.$priceInput = this.$el.find('input.js-price-input-group');
        this.listPrice = parseInt(this.$el.find('.js-list-price').data('price'), 10);

        const $form = this.$el.find(this.formSelector);
        const $formGroup = $form.find('.js-form-group');
        const $quantityInput = $form.find('[name="quantity"]');

        // Ignore non-numeric characters when checking input values.
        // Helpful in the case of a user pasting a string like '$150' into the price input.
        const parseInputInt = ($input) => ($input.length > 0 ? parseInt($input.val().replaceAll(/\D/g, ''), 10) : 0);
        // Current quantity for the offer, or 1 if not set
        const getQuantity = () => parseInputInt($quantityInput) || $form.data('quantity') || 1;

        const checkPrice = async (price) => {
            const quantity = getQuantity();

            try {
                const response = await validateOfferPrice({
                    price,
                    productId: this.productId,
                    quantity,
                });

                const errorMessage = response?.products?.[0]?.price?.errors?.[0]?.display;
                FormUtils.setFormGroupErrorMessage($formGroup, errorMessage);
            } catch (error) {
                handleAsyncError(error);
            }
        };

        // Fix the click targets in input groups since half the 'input' is a <span>.
        $form.find('.js-percent-input-group').on('click', () => this.$percentInput.focus());
        $form.find('.js-price-input-group').on('click', () => this.$priceInput.focus());

        const $groupedInputs = this.$percentInput.add(this.$priceInput);

        const toggleFocus = (shouldFocus) => {
            $formGroup.find('.js-input-group').toggleClass('focused', shouldFocus);
        };

        $groupedInputs.on('focus', () => toggleFocus(true));
        $groupedInputs.on('focusout', () => toggleFocus(false));

        // Only allow numeric input in price/percent
        $groupedInputs.on('keypress', (e) => {
            if (!KeyUtils.isNumeric(e.which)) {
                e.preventDefault();
            }
        });

        if (this.shouldValidateOnBlur) {
            $groupedInputs.on('blur', ({ relatedTarget }) => {
                if (
                    !relatedTarget ||
                    (relatedTarget.getAttribute('role') !== 'button' && relatedTarget.tagName !== 'BUTTON')
                ) {
                    // Don't check price if user clicked a recommended offer or the submit button.
                    // The submit button does its own price check.
                    // N.B. the div 'button' needs a tab index for relatedTarget to be correct.
                    checkPrice(parseInputInt(this.$priceInput));
                }
            });
        }

        this.$percentInput.on('keyup', () => {
            const percentOff = parseInputInt(this.$percentInput);
            const price = percentOff >= 0 && percentOff <= 100 ? getDiscountedPrice(this.listPrice, percentOff) : '';
            this.$priceInput.val(price);
            this.onPriceChange(price);
        });

        this.$priceInput.on('keyup', () => {
            const price = parseInputInt(this.$priceInput);
            this.$percentInput.val(getValidPercentValue(price, this.listPrice));
            this.onPriceChange(price);
        });

        // Submit the offer, which may be part of a higher level recommendation.
        // If the user set a custom price, then this will first check that price is valid.
        $form.on('submit', async (e) => {
            e.preventDefault();
            const $selectedOffer = this.$el.find('.js-selected');
            const isRecommendedOffer = $selectedOffer.length > 0;

            let price;
            if (isRecommendedOffer) {
                price = parseInt($selectedOffer.data('price'), 10);
            } else {
                price = parseInputInt(this.$priceInput);
                await checkPrice(price);
            }

            const quantity = getQuantity();

            if ($form.find('.has-error').length === 0) {
                const eventProperties = getAmplitudeEventPropertiesForProduct(new ProductModel(getProductObject()));

                if (isRecommendedOffer) {
                    eventProperties[AmplitudeEventProperty.POSITION] = CheckoutPosition.MAO_MODAL;
                    eventProperties.is_recommended_offer = isRecommendedOffer;
                }

                const params = new URLSearchParams();
                const recommendedOffersForm = this.$el.find('#recommended-offers-form');
                params.set('offer_type', recommendedOffersForm.data('offer-type'));

                const source =
                    new URLSearchParams(document.location.search).get('source') || recommendedOffersForm.data('source');
                if (source) {
                    params.set('source', source);
                }

                if (price > 0) {
                    params.set('price', price.toString());
                }

                if (quantity > 1) {
                    params.set('quantity', quantity.toString());
                }

                const privateDealId = this.privateDealId || recommendedOffersForm.data('private-deal-id');
                if (privateDealId) {
                    params.set('private_deal_id', privateDealId.toString());
                }

                if (price) {
                    eventProperties.percent_off = Math.floor(100 - (price / this.listPrice) * 100);
                } else {
                    eventProperties.percent_off = 'N/A';
                }

                logAmplitudeButtonClickEvent('review offer', eventProperties);
                this.onSubmit($form, params);
            }
        });
    }

    /**
     * @param {null|number} defaultPrice The default offer price value to show. The price input will be empty when null.
     */
    setDefaultPrice(defaultPrice) {
        const percentOff = getPercentOff(this.listPrice, defaultPrice);
        if (percentOff) {
            this.$priceInput.val(defaultPrice);
            this.$percentInput.val(percentOff);
        }
    }
}
